import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../../shared/api'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RefoodContract {
  contract_id: number
  vertrag_nr: string
  leistungsort_kd_nr: string
  school_id: number | null
  school_name: string | null
  kunde_bez: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  bin_size_L: number | null
  bin_count: number
  material: string
  rhythm: 'weekly' | 'biweekly' | 'monthly' | 'on_demand'
  service_day: string | null
  price_per_pickup_eur: number | null
  effective_from: string
  effective_to: string | null
  status: 'active' | 'suspended' | 'terminated' | 'pending'
  driver_notes_text: string | null
  rechnungsempfaenger_kd_nr: string | null
  rechnungsempfaenger_name: string | null
}

export interface RefoodKlaerung {
  klaerung_id: number
  source_file_name: string
  source_row_number: number
  vertrag_nr: string
  leistungsort_kd_nr: string
  kunde_bez: string | null
  strasse: string | null
  plz: string | null
  ort: string | null
  bin_size_L: number | null
  material: string | null
  rhythm: string | null
  service_day: string | null
  driver_notes_text: string | null
  suggested_school_id: number | null
  suggested_school_name: string | null
  confidence: number | null
  status: 'open' | 'resolved' | 'ignored'
  audit_created_at: string
}

export interface RefoodSetting {
  setting_key: string
  setting_value: string
  description: string | null
  audit_modified_at: string
  audit_modified_by: string | null
}

export interface RefoodPricing {
  pricing_id: number
  bin_size_L: number
  material: string
  price_per_pickup_eur: number
  effective_from: string
  effective_to: string | null
}

export interface RefoodPickup {
  pickup_id: number
  contract_id: number
  scheduled_date: string
  status: 'planned' | 'cancelled_by_us' | 'cancelled_by_refood' | 'done' | 'reconciled'
  est_cost_eur: number | null
  actual_cost_eur: number | null
  cancel_reason: string | null
  cancelled_at: string | null
  school_id: number | null
  school_name: string | null
  kunde_bez: string | null
  leistungsort_kd_nr: string
  bin_size_L: number | null
  bin_count: number
  material: string
  rhythm: 'weekly' | 'biweekly' | 'monthly' | 'on_demand'
  service_day: string | null
  price_per_pickup_eur: number | null
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export function useRefoodContracts() {
  return useQuery({
    queryKey: ['refood-contracts'],
    queryFn: () => api.get<{ contracts: RefoodContract[] }>('/api/refood/contracts'),
    staleTime: 30_000,
  })
}

// ── Rechnungen (Phase C iter 5) ───────────────────────────────────────────

export interface RefoodInvoiceSummary {
  invoice_id: number
  invoice_number: string
  invoice_date: string
  buchungsdatum: string | null
  kunden_nr: string | null
  period_from: string | null
  period_to: string | null
  total_net_eur: number
  total_vat_eur: number | null
  vat_rate: number | null
  total_gross_eur: number
  sepa_mandat: string | null
  sepa_due_date: string | null
  status: 'imported' | 'matched' | 'reconciled' | 'flagged'
  reconciliation_status: 'pending' | 'matched' | 'partial' | 'orphan'
  parser_version: string | null
  audit_created_at: string
  audit_created_by: string | null
  line_count: number
  matched_count: number
}

export interface RefoodInvoiceLine {
  line_id: number
  leistungsort_kd_nr: string
  pickup_date: string | null
  kind: 'verwertung' | 'leerfahrt' | 'other'
  quantity: number
  unit: string
  unit_price_eur: number
  amount_net_eur: number
  auftrag_nr: string | null
  befoerderer: string | null
  empfaenger: string | null
  material: string | null
  bin_size_L: number | null
  matched_pickup_id: number | null
  school_id: number | null
  school_name: string | null
  kunde_bez: string | null
  kostenstelle_id: number | null
  pickup_scheduled_date: string | null
  pickup_status: string | null
}

export interface RefoodInvoicePerOrt {
  leistungsort_kd_nr: string
  school_id: number | null
  school_name: string | null
  kostenstelle_id: number | null
  total_net_eur: number
  verwertung_stk: number
  leerfahrt_pau: number
  line_count: number
  matched_count: number
}

export interface RefoodInvoiceUnknownLeistungsort {
  kd_nr: string
  name: string | null
  address: string | null
  line_count: number
  total_net_eur: number
}

// Mirror of unknown_summary but for Kd.-Nrs that DID resolve — surfaced so
// the wizard can show every Kd.-Nr. with its assigned school + override
// option (catches silent SSI mismaps before commit).
export interface RefoodInvoiceResolvedLeistungsort {
  kd_nr: string
  name: string | null
  address: string | null
  school_id: string | null
  school_name: string | null
  kostenstelle_id: number | null
  /** mapping = set by current upload's X-Mappings header
   *  ssi     = pre-existing dim.SchoolSupplierIdentifier row
   *  operative = mapped to an operative KS (not a school) */
  source: 'mapping' | 'ssi' | 'operative'
  line_count: number
  total_net_eur: number
}

export interface RefoodConfidenceIssue {
  severity: 'error' | 'warning'
  code: string
  message: string
}

export interface RefoodConfidence {
  /** 0.0 – 1.0; 1.0 = perfect, eligible for auto-commit when threshold met */
  score: number
  issues: RefoodConfidenceIssue[]
}

export interface RefoodTaxBucket {
  rate: string
  rechnung_net: number
  rechnung_vat: number
  erkannt_net: number
  erkannt_vat: number
  passt: boolean
}

export type RefoodImportState =
  | 'fresh'
  | 'already-imported-identical'
  | 'already-imported-changed'

export interface RefoodInvoicePreview {
  dryRun: true
  fileName: string
  duplicate: boolean
  /** Re-import handling state. 'fresh' = first-time, identical/changed
   *  surface a Bereits-importiert pill in the UI. */
  state: RefoodImportState
  /** SHA-256 of canonical parse projection — used to detect identical
   *  re-uploads vs content-changed re-imports. */
  content_hash: string
  confidence: RefoodConfidence
  tax_breakdown: RefoodTaxBucket[]
  resolved_summary: RefoodInvoiceResolvedLeistungsort[]
  invoice: {
    invoice_number: string
    invoice_date: string
    buchungsdatum: string | null
    kunden_nr: string | null
    period_from: string | null
    period_to: string | null
    total_net_eur: number
    total_vat_eur: number
    vat_rate: number
    total_gross_eur: number
    sepa_mandat: string | null
    sepa_due_date: string | null
  }
  lines: Array<{
    leistungsort_kd_nr: string
    pickup_date: string | null
    kind: 'verwertung' | 'leerfahrt' | 'other'
    quantity: number
    unit: string
    unit_price_eur: number
    amount_net_eur: number
    material: string | null
    bin_size_L: number | null
  }>
  leistungsorte: Array<{
    kd_nr: string
    name: string | null
    address: string | null
    known: boolean
    school_id: number | null
    school_name: string | null
  }>
  warnings: string[]
  unknown_count: number
  unknown_lines: number
  unknown_net_eur: number
  unknown_summary: RefoodInvoiceUnknownLeistungsort[]
}

export interface RefoodInvoiceCommitResult {
  ok: true
  invoiceId: number
  invoice_number: string
  invoice_date: string
  total_net_eur: number
  total_gross_eur: number
  line_count: number
  matched: number
  unmatched: number
  reconciliation_status: 'pending' | 'matched' | 'partial' | 'orphan'
  warnings: string[]
  leistungsorte: Array<{
    kd_nr: string
    name: string | null
    address: string | null
    known: boolean
    school_id: number | null
    school_name: string | null
  }>
  unknown_count: number
  unknown_lines: number
  unknown_net_eur: number
  unknown_summary: RefoodInvoiceUnknownLeistungsort[]
}

export interface RefoodBySchoolRow {
  school_id: string
  period: string                // 'YYYY-MM'
  total_net_eur: number
  verwertung_net_eur: number
  leerfahrt_net_eur: number
  line_count: number
  matched_count: number
}

export interface RefoodBySchoolResponse {
  period: string | null
  perSchool: RefoodBySchoolRow[]
  total_net_eur: number
}

export function useRefoodBySchool(period?: string | null) {
  return useQuery({
    queryKey: ['refood-by-school', period ?? null],
    queryFn: () => {
      const qs = period ? `?period=${encodeURIComponent(period)}` : ''
      return api.get<RefoodBySchoolResponse>(`/api/refood/by-school${qs}`)
    },
    staleTime: 60_000,
  })
}

// Per-invoice rollup for a school × period — powers the Entsorgung section's
// inline invoice list on the Auswertung deep-dive. Each invoice carries its
// share for this school + the underlying pickup lines (already filtered to
// the school × period scope on the backend).
export interface RefoodInvoiceBySchoolLine {
  line_id: number
  invoice_id: number
  leistungsort_kd_nr: string
  pickup_date: string | null
  kind: 'verwertung' | 'leerfahrt' | 'other'
  quantity: number
  unit: string
  unit_price_eur: number
  amount_net_eur: number
}

export interface RefoodInvoiceBySchool {
  invoice_id: number
  invoice_number: string
  invoice_date: string
  invoice_total_net_eur: number
  invoice_total_gross_eur: number
  school_net_eur: number
  school_verwertung_eur: number
  school_leerfahrt_eur: number
  school_line_count: number
  lines: RefoodInvoiceBySchoolLine[]
}

export interface RefoodInvoicesBySchoolResponse {
  schoolId: number
  period: string | null
  invoices: RefoodInvoiceBySchool[]
}

export function useRefoodInvoicesBySchool(schoolId: number | null | undefined, period?: string | null) {
  return useQuery({
    queryKey: ['refood-invoices-by-school', schoolId ?? null, period ?? null],
    enabled: schoolId != null,
    queryFn: () => {
      const qs = new URLSearchParams()
      if (schoolId != null) qs.set('schoolId', String(schoolId))
      if (period) qs.set('period', period)
      return api.get<RefoodInvoicesBySchoolResponse>(`/api/refood/invoices/by-school?${qs.toString()}`)
    },
    staleTime: 60_000,
  })
}

export function useRefoodInvoices() {
  return useQuery({
    queryKey: ['refood-invoices'],
    queryFn: () => api.get<{ invoices: RefoodInvoiceSummary[] }>('/api/refood/invoices'),
    staleTime: 30_000,
  })
}

export function useRefoodInvoiceDetail(id: number | string | null) {
  // BIGINT columns serialise as JSON STRING via mssql, so callers may pass
  // either '3' or 3. Coerce defensively so the enabled check works either way.
  const numId = id == null ? null : Number(id)
  return useQuery({
    queryKey: ['refood-invoice', numId],
    queryFn: () => api.get<{
      invoice: RefoodInvoiceSummary
      lines: RefoodInvoiceLine[]
      perLeistungsort: RefoodInvoicePerOrt[]
    }>(`/api/refood/invoices/${numId}`),
    enabled: numId != null && Number.isFinite(numId) && numId > 0,
    staleTime: 60_000,
  })
}

// Mirror of ChefsCulinar's PzDecision pattern. Each unknown Kd.-Nr. is in
// exactly one of three states; the UI gates "Importieren" until every row
// has been decided.
//   - 'skip'             → don't route this line; school_id stays NULL on import.
//                          No SSI row written. Recurrence on the next invoice.
//   - mode='once'        → use the chosen target for THIS upload only.
//                          No SSI row written. Recurrence on the next invoice.
//   - mode='permanent'   → write a dim.SchoolSupplierIdentifier row so future
//                          invoices auto-resolve this Kd.-Nr.
export type RefoodKdNrMappingMode = 'once' | 'permanent'
export type RefoodKdNrMapping =
  | { kind: 'school';    school_id: string; school_name?: string; mode: RefoodKdNrMappingMode; auto?: boolean }
  | { kind: 'operative'; kostenstelle_id: number; category: string; label?: string; mode: RefoodKdNrMappingMode; auto?: boolean }
  | { kind: 'skip' }
export type RefoodKdNrMappings = Record<string, RefoodKdNrMapping>

export function useUploadRefoodInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, dryRun, allowDuplicate, mappings }: {
      file: File
      dryRun?: boolean
      allowDuplicate?: boolean
      mappings?: RefoodKdNrMappings
    }) => {
      const buf = await file.arrayBuffer()
      const qs = new URLSearchParams()
      if (dryRun) qs.set('dryRun', '1')
      if (allowDuplicate) qs.set('allowDuplicate', '1')
      const url = `/api/refood/invoices/upload${qs.toString() ? '?' + qs.toString() : ''}`
      const headers: Record<string, string> = {
        'Content-Type': 'application/pdf',
        'X-Filename':   file.name,
      }
      if (mappings && Object.keys(mappings).length > 0) {
        headers['X-Mappings'] = JSON.stringify(mappings)
      }
      const res = await window.fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: buf,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw Object.assign(new Error(json.error || `HTTP ${res.status}`), json)
      return json as RefoodInvoicePreview | RefoodInvoiceCommitResult
    },
    onSuccess: (data) => {
      if ('ok' in data && data.ok) {
        qc.invalidateQueries({ queryKey: ['refood-invoices'] })
        qc.invalidateQueries({ queryKey: ['refood-pickups'] })   // status flipped to reconciled
      }
    },
  })
}

// ── Operative Kostenstelle list (for unknown-Kd.Nr. mapping cascade) ────────
// Returns all kind='operative' rows across all firmas — used by the inline
// mapping form in the Re-Food invoice import. Re-Food bills both firmas so
// we don't filter by firma here.

export interface RefoodOperativeKs {
  id: number
  code: string
  name: string
  firma: string
  operative_category: string | null
  datev_export_code: string | null
}

export function useOperativeKostenstellen() {
  return useQuery<RefoodOperativeKs[]>({
    queryKey: ['refood', 'operative-ks'],
    queryFn: async () => {
      // Pull both firmas — Re-Food is cross-firma.
      const [gmbh, sc] = await Promise.all([
        fetch('/api/dim/kostenstelle?kind=operative&firma=GmbH&active_only=1', { credentials: 'include' }).then(r => r.json()),
        fetch('/api/dim/kostenstelle?kind=operative&firma=Schulcatering%20GmbH&active_only=1', { credentials: 'include' }).then(r => r.json()),
      ])
      return [...(Array.isArray(gmbh) ? gmbh : []), ...(Array.isArray(sc) ? sc : [])]
    },
    staleTime: 5 * 60_000,
  })
}

// ── Wochen-Grid (Phase 4) ────────────────────────────────────────────────────

export type WeekCellState = 'green' | 'yellow' | 'red' | 'gray'

export interface RefoodWeekKw {
  year: number
  week: number
  label: string
  monday_iso: string
  sunday_iso: string
}

export interface RefoodWeekCell {
  kw: string
  state: WeekCellState
  amount: number | null
  /** Sum of Verwertung lines (recycling/material collected) in this KW. */
  amount_verwertung?: number
  /** Sum of Leerfahrt lines (empty-trip charges) in this KW. */
  amount_leerfahrt?: number
  verwertung_count?: number
  leerfahrt_count?: number
  pickup_count: number
  reconciled_count: number
  cancelled_count: number
  planned_count: number
}

export interface RefoodWeekContractRow {
  contract_id: number
  school_id: number | null
  school_name: string
  leistungsort_kd_nr: string
  bin_count: number
  bin_size_L: number | null
  material: string
  rhythm: 'weekly' | 'biweekly' | 'monthly' | 'on_demand'
  service_day: string | null
  status: 'active' | 'pending' | 'suspended' | 'terminated'
  cells: RefoodWeekCell[]
}

export interface RefoodWeekGridResponse {
  from: string
  to: string
  kws: RefoodWeekKw[]
  contracts: RefoodWeekContractRow[]
}

export function useRefoodWeekGrid(from: string, to: string) {
  return useQuery({
    queryKey: ['refood-week-grid', from, to],
    queryFn: () => api.get<RefoodWeekGridResponse>(`/api/refood/week-grid?from=${from}&to=${to}`),
    enabled: !!from && !!to,
    staleTime: 30_000,
  })
}

// ── Cancellation template / bulk upload ──────────────────────────────────────

export interface RefoodCancellationPreview {
  dryRun: true
  kws: Array<{ year: number; week: number }>
  cancellations: Array<{
    row_number: number
    leistungsort_kd_nr: string
    school_name: string | null
    kw_year: number
    kw_week: number
    week_dates: string[]
    contract_ids: number[]
  }>
  skipped: Array<{ row_number: number; leistungsort_kd_nr: string; reason: string }>
  warnings: string[]
}

export interface RefoodCancellationResult {
  ok: true
  actionId?: number
  cancellations: number
  cancelledPickups?: number
  generatedPickups?: number
  warnings?: string[]
  message?: string
}

export async function downloadCancellationTemplate(from: string, to: string): Promise<Blob> {
  const res = await window.fetch(`/api/refood/cancellation-template?from=${from}&to=${to}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`Vorlage konnte nicht erstellt werden (${res.status})`)
  return res.blob()
}

export async function downloadWochenExport(from: string, to: string): Promise<Blob> {
  const res = await window.fetch(`/api/refood/week-grid/export?from=${from}&to=${to}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error(`Excel konnte nicht erstellt werden (${res.status})`)
  return res.blob()
}

export function useUploadCancellation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, dryRun }: { file: File; dryRun?: boolean }) => {
      const buf = await file.arrayBuffer()
      const qs = dryRun ? '?dryRun=1' : ''
      const res = await window.fetch(`/api/refood/cancellation-upload${qs}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/octet-stream', 'X-Filename': file.name },
        body: buf,
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw Object.assign(new Error(json.error || `HTTP ${res.status}`), json)
      return json as RefoodCancellationPreview | RefoodCancellationResult
    },
    onSuccess: (data) => {
      if ('ok' in data && data.ok) {
        qc.invalidateQueries({ queryKey: ['refood-pickups'] })
        qc.invalidateQueries({ queryKey: ['refood-week-grid'] })
        qc.invalidateQueries({ queryKey: ['refood-actions'] })
      }
    },
  })
}

export function useRefoodPickups(opts: { from: string; to: string; schoolId?: number | null }) {
  const { from, to, schoolId } = opts
  const qs = new URLSearchParams({ from, to })
  if (schoolId) qs.set('schoolId', String(schoolId))
  return useQuery({
    queryKey: ['refood-pickups', from, to, schoolId ?? null],
    queryFn: () => api.get<{ pickups: RefoodPickup[]; from: string; to: string; schoolId: number | null }>(
      `/api/refood/pickups?${qs.toString()}`),
    staleTime: 30_000,
    enabled: !!from && !!to,
  })
}

export function useRefoodContract(id: number | null) {
  return useQuery({
    queryKey: ['refood-contract', id],
    queryFn: () => api.get<{ contract: RefoodContract }>(`/api/refood/contracts/${id}`),
    enabled: typeof id === 'number' && id > 0,
    staleTime: 30_000,
  })
}

export type ContractPatch = Partial<Pick<RefoodContract,
  | 'bin_size_L' | 'bin_count' | 'material' | 'rhythm' | 'service_day'
  | 'price_per_pickup_eur' | 'driver_notes_text' | 'status' | 'school_id'
  | 'kunde_bez' | 'strasse' | 'plz' | 'ort'
>>

export function usePatchContract(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ContractPatch) =>
      api.patch<{ ok: true; contract: RefoodContract }>(`/api/refood/contracts/${id}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['refood-contracts'] })
      qc.invalidateQueries({ queryKey: ['refood-contract', id] })
    },
  })
}

export function useRefoodKlaerung() {
  return useQuery({
    queryKey: ['refood-klaerung'],
    queryFn: () => api.get<{ klaerung: RefoodKlaerung[] }>('/api/refood/klaerung'),
    staleTime: 30_000,
  })
}

export function useResolveKlaerung() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, schoolId, action }: { id: number; schoolId: number | null; action: 'commit' | 'ignore' }) =>
      api.post<{ ok: true; action: string }>(`/api/refood/klaerung/${id}/resolve`, { schoolId, action }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['refood-klaerung'] })
      qc.invalidateQueries({ queryKey: ['refood-contracts'] })
    },
  })
}

export function useRefoodSettings() {
  return useQuery({
    queryKey: ['refood-settings'],
    queryFn: () => api.get<{ settings: RefoodSetting[] }>('/api/refood/settings'),
    staleTime: 60_000,
  })
}

export function usePatchSetting() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.patch<{ ok: true }>(`/api/refood/settings/${encodeURIComponent(key)}`, { value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refood-settings'] }),
  })
}

export function useRefoodPricing() {
  return useQuery({
    queryKey: ['refood-pricing'],
    queryFn: () => api.get<{ pricing: RefoodPricing[] }>('/api/refood/pricing'),
    staleTime: 60_000,
  })
}

export function usePatchPricing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ binSize, material, price }: { binSize: number; material: string; price: number }) =>
      api.patch<{ ok: true }>(
        `/api/refood/pricing/${binSize}/${encodeURIComponent(material)}`,
        { price_per_pickup_eur: price },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refood-pricing'] }),
  })
}

/** Correct a single historical pricing row in-place (no SCD2 versioning). */
export function useCorrectPricing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ pricingId, price }: { pricingId: number; price: number }) =>
      api.patch<{ ok: true }>(
        `/api/refood/pricing/by-id/${pricingId}`,
        { price_per_pickup_eur: price },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refood-pricing'] }),
  })
}

export interface CreatePricingPayload {
  bin_size_L: number
  material: string
  price_per_pickup_eur: number
  effective_from: string         // YYYY-MM-DD
  effective_to?: string | null   // YYYY-MM-DD or null/empty for active
}

/** Insert a brand-new pricing row (new combo or backfill). */
export function useCreatePricing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreatePricingPayload) =>
      api.post<{ ok: true; pricing_id: number }>(`/api/refood/pricing`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refood-pricing'] }),
  })
}

/** Permanently delete a pricing row by id. */
export function useDeletePricing() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pricingId: number) =>
      api.delete<{ ok: true }>(`/api/refood/pricing/by-id/${pricingId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['refood-pricing'] }),
  })
}

// Uploads the raw xlsx bytes (Content-Type: application/octet-stream) along with
// a filename hint via x-filename header.
export function useImportRefood() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, dryRun }: { file: File; dryRun?: boolean }) => {
      const buf = await file.arrayBuffer()
      const qs = dryRun ? '?dryRun=1' : ''
      const res = await fetch(`/api/refood/import${qs}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/octet-stream',
          'x-filename':   file.name,
        },
        body: buf,
        credentials: 'include',
      })
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({ error: `HTTP ${res.status}` }))
        throw new Error(errBody.error || `HTTP ${res.status}`)
      }
      return res.json() as Promise<{
        ok?: true
        dryRun?: true
        fileName?: string
        parsed: number
        autoMapped: number
        klaerung: number
        samples?: unknown
      }>
    },
    onSuccess: (_, vars) => {
      if (!vars.dryRun) {
        qc.invalidateQueries({ queryKey: ['refood-contracts'] })
        qc.invalidateQueries({ queryKey: ['refood-klaerung'] })
      }
    },
  })
}

// ── Settings convenience getter ───────────────────────────────────────────────
export function getSetting(settings: RefoodSetting[] | undefined, key: string): string | null {
  if (!settings) return null
  return settings.find(s => s.setting_key === key)?.setting_value ?? null
}

// ── Ferien-Pause action (Phase B.1) ──────────────────────────────────────────

export interface FerienPausePayload {
  from: string          // YYYY-MM-DD
  to:   string          // YYYY-MM-DD
  schoolIds: number[]
  reason?: string
}

export interface FerienPausePreview {
  dryRun: true
  affectedContracts: number
  affectedSchools: number
  preview: { subject: string; body: string; to: string; mailtoUrl: string }
}

export interface FerienPauseResult {
  ok: true
  actionId: number
  draftId: number
  affectedContracts: number
  cancelledPickups: number
  mailtoUrl: string
  subject: string
  body: string
  to: string
}

export function useFerienPause() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ payload, dryRun }: { payload: FerienPausePayload; dryRun?: boolean }) =>
      api.post<FerienPausePreview | FerienPauseResult>(
        `/api/refood/actions/ferien-pause${dryRun ? '?dryRun=1' : ''}`,
        payload,
      ),
    onSuccess: (_, vars) => {
      if (!vars.dryRun) {
        qc.invalidateQueries({ queryKey: ['refood-contracts'] })
      }
    },
  })
}

// ── Schuljahr (Hamburg quick-select for the Ferien picker) ───────────────────

export interface SchuljahrRow {
  schuljahr: string
  bundesland: string
  von: string
  bis: string
  syncedAt: string
}

export function useSchuljahrHH() {
  return useQuery({
    queryKey: ['schuljahr', 'HH'],
    queryFn: () => api.get<SchuljahrRow[]>(`/api/schuljahr?bundesland=HH`),
    staleTime: 60 * 60 * 1000,
  })
}

// ── Ferien windows (per-Bundesland holiday spans) ────────────────────────────

export interface RefoodFerienWindow {
  ferien_id: string
  name: string
  bundesland: string
  von: string        // YYYY-MM-DD
  bis: string        // YYYY-MM-DD
  kw_von: string     // YYYY-Wnn
  kw_bis: string     // YYYY-Wnn
  days: number
}

export interface RefoodFerienWindowsResponse {
  bundesland: string
  since_year: number
  windows: RefoodFerienWindow[]
}

export function useRefoodFerienWindows(bundesland: string) {
  return useQuery({
    queryKey: ['refood-ferien-windows', bundesland],
    queryFn: () => api.get<RefoodFerienWindowsResponse>(`/api/refood/ferien-windows?bundesland=${encodeURIComponent(bundesland)}`),
    enabled: !!bundesland,
    staleTime: 60 * 60 * 1000,
  })
}

// ── Resize / Terminate actions (Phase B.3) ───────────────────────────────────

export interface ResizePayload {
  contractId: number
  newBinSizeL: number
  newBinCount: number
  effectiveFrom: string   // YYYY-MM-DD
  reason?: string
}

export interface TerminatePayload {
  contractId: number
  effectiveTo: string     // YYYY-MM-DD
  reason?: string
}

export interface ActionPreview {
  dryRun: true
  preview: { subject: string; body: string; to: string; mailtoUrl: string }
}

export interface ActionResult {
  ok: true
  actionId: number
  draftId: number
  mailtoUrl: string
  subject: string
  body: string
  to: string
  cancelledPickups?: number
}

export function useResizeContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ payload, dryRun }: { payload: ResizePayload; dryRun?: boolean }) =>
      api.post<ActionPreview | ActionResult>(
        `/api/refood/actions/resize${dryRun ? '?dryRun=1' : ''}`,
        payload,
      ),
    onSuccess: (_, vars) => {
      if (!vars.dryRun) {
        qc.invalidateQueries({ queryKey: ['refood-contracts'] })
        qc.invalidateQueries({ queryKey: ['refood-actions'] })
      }
    },
  })
}

export function useTerminateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ payload, dryRun }: { payload: TerminatePayload; dryRun?: boolean }) =>
      api.post<ActionPreview | ActionResult>(
        `/api/refood/actions/terminate${dryRun ? '?dryRun=1' : ''}`,
        payload,
      ),
    onSuccess: (_, vars) => {
      if (!vars.dryRun) {
        qc.invalidateQueries({ queryKey: ['refood-contracts'] })
        qc.invalidateQueries({ queryKey: ['refood-actions'] })
      }
    },
  })
}

export interface NewOrderPayload {
  schoolId: number
  kundeBez?: string
  strasse?: string
  plz?: string
  ort?: string
  binSizeL: number
  binCount: number
  material: string
  rhythm: 'weekly' | 'biweekly' | 'monthly' | 'on_demand'
  serviceDay?: string | null
  pricePerPickupEur?: number | null
  effectiveFrom: string   // YYYY-MM-DD
  reason?: string
}

export function useNewOrderContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ payload, dryRun }: { payload: NewOrderPayload; dryRun?: boolean }) =>
      api.post<ActionPreview | ActionResult>(
        `/api/refood/actions/new-order${dryRun ? '?dryRun=1' : ''}`,
        payload,
      ),
    onSuccess: (_, vars) => {
      if (!vars.dryRun) {
        qc.invalidateQueries({ queryKey: ['refood-actions'] })
      }
    },
  })
}

// ── Aktionen history ─────────────────────────────────────────────────────────

export interface RefoodActionRow {
  action_id: number
  action_type: 'pause_window' | 'resume' | 'resize' | 'new_order' | 'terminate'
  school_id: number | null
  contract_id: number | null
  date_range_from: string | null
  date_range_to: string | null
  payload_json: string | null
  created_by: string
  created_at: string
  email_draft_id: number | null
  to_address: string | null
  subject: string | null
  body_text: string | null
  mailto_url: string | null
  draft_status: 'draft' | 'ready' | 'mailto_opened' | null
  mailto_opened_at: string | null
  school_name: string | null
  leistungsort_kd_nr: string | null
}

export function useRefoodActions(limit = 50) {
  return useQuery({
    queryKey: ['refood-actions', limit],
    queryFn: () => api.get<{ actions: RefoodActionRow[] }>(`/api/refood/actions?limit=${limit}`),
    staleTime: 15_000,
  })
}

// ── Schul-Anfragen (M27 phase 2, migration 367) ──────────────────────────────
// Requests that schools send from the school portal; the office answers here.

export type SchulAnfrageArt = 'zusaetzlich' | 'absage' | 'mehr' | 'weniger'
export type SchulAnfrageStatus = 'offen' | 'bestaetigt' | 'abgelehnt' | 'erledigt'
export type SchulAnfrageFilter = 'aktiv' | SchulAnfrageStatus | 'alle'

export interface SchulAnfrage {
  anfrage_id: string
  school_id: string
  school_name: string | null
  contract_id: string
  leistungsort_kd_nr: string | null
  bin_size_L: number | null
  bin_count: number | null
  material: string | null
  service_day: string | null
  art: SchulAnfrageArt
  /** The day the school means (YYYY-MM-DD). */
  datum: string | null
  notiz: string | null
  kuerzel: string | null
  /** Label of the school card the request came from. */
  karte: string | null
  status: SchulAnfrageStatus
  erstellt_am: string | null
  /** The office's note; the school sees it. */
  antwort: string | null
  bearbeitet_von: string | null
  bearbeitet_am: string | null
}

export interface SchulAnfragenData {
  anfragen: SchulAnfrage[]
  /** Counts over ALL requests (for the tab badge), not only the filtered list. */
  offen: number
  bestaetigt: number
}

export function useRefoodSchulAnfragen(filter: SchulAnfrageFilter) {
  return useQuery({
    queryKey: ['refood-schul-anfragen', filter],
    queryFn: () => api.get<SchulAnfragenData>(`/api/refood/schul-anfragen?status=${filter}`),
    staleTime: 20_000,
    // Before migration 367 ran the route answers 503: stop polling then.
    refetchInterval: (q) => ((q.state.error as { status?: number } | null)?.status === 503 ? false : 60_000),
    retry: 1,
  })
}

export function useBearbeiteSchulAnfrage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status, antwort }: { id: string; status: Exclude<SchulAnfrageStatus, 'offen'>; antwort: string }) =>
      api.patch<{ ok: true }>(`/api/refood/schul-anfragen/${id}`, { status, antwort: antwort.trim() || null }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['refood-schul-anfragen'] }) },
  })
}
